import { useEffect } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AGENT_DASHBOARD } from "@/constants/testIds";
import "./AgentDashboard.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Agent-key → display config. Order controls card order. */
const AGENT_CARDS = [
  {
    key: "market_signals",
    label: "Market Signals",
    tone: "cyan",
    testCard: AGENT_DASHBOARD.cardMarketSignals,
    testStatus: AGENT_DASHBOARD.statusMarketSignals,
    testGeneratedAt: AGENT_DASHBOARD.generatedAtMarketSignals,
    testHeadline: AGENT_DASHBOARD.headlineMarketSignals,
    testAttrCard: AGENT_DASHBOARD.attributionCardMarketSignals,
    testAttrClicks: AGENT_DASHBOARD.attributionClicksMarketSignals,
    testAttrConversions: AGENT_DASHBOARD.attributionConversionsMarketSignals,
    testAttrRate: AGENT_DASHBOARD.attributionRateMarketSignals,
    testAttrRevenue: AGENT_DASHBOARD.attributionRevenueMarketSignals,
  },
  {
    key: "offer_builder",
    label: "Offer Builder",
    tone: "gold",
    testCard: AGENT_DASHBOARD.cardOfferBuilder,
    testStatus: AGENT_DASHBOARD.statusOfferBuilder,
    testGeneratedAt: AGENT_DASHBOARD.generatedAtOfferBuilder,
    testHeadline: AGENT_DASHBOARD.headlineOfferBuilder,
    testAttrCard: AGENT_DASHBOARD.attributionCardOfferBuilder,
    testAttrClicks: AGENT_DASHBOARD.attributionClicksOfferBuilder,
    testAttrConversions: AGENT_DASHBOARD.attributionConversionsOfferBuilder,
    testAttrRate: AGENT_DASHBOARD.attributionRateOfferBuilder,
    testAttrRevenue: AGENT_DASHBOARD.attributionRevenueOfferBuilder,
  },
  {
    key: "youtube_content",
    label: "YouTube Content",
    tone: "magenta",
    testCard: AGENT_DASHBOARD.cardYoutubeContent,
    testStatus: AGENT_DASHBOARD.statusYoutubeContent,
    testGeneratedAt: AGENT_DASHBOARD.generatedAtYoutubeContent,
    testHeadline: AGENT_DASHBOARD.headlineYoutubeContent,
    testAttrCard: AGENT_DASHBOARD.attributionCardYoutubeContent,
    testAttrClicks: AGENT_DASHBOARD.attributionClicksYoutubeContent,
    testAttrConversions: AGENT_DASHBOARD.attributionConversionsYoutubeContent,
    testAttrRate: AGENT_DASHBOARD.attributionRateYoutubeContent,
    testAttrRevenue: AGENT_DASHBOARD.attributionRevenueYoutubeContent,
  },
  {
    key: "lead_magnet",
    label: "Lead Magnet",
    tone: "lime",
    testCard: AGENT_DASHBOARD.cardLeadMagnet,
    testStatus: AGENT_DASHBOARD.statusLeadMagnet,
    testGeneratedAt: AGENT_DASHBOARD.generatedAtLeadMagnet,
    testHeadline: AGENT_DASHBOARD.headlineLeadMagnet,
    testAttrCard: AGENT_DASHBOARD.attributionCardLeadMagnet,
    testAttrClicks: AGENT_DASHBOARD.attributionClicksLeadMagnet,
    testAttrConversions: AGENT_DASHBOARD.attributionConversionsLeadMagnet,
    testAttrRate: AGENT_DASHBOARD.attributionRateLeadMagnet,
    testAttrRevenue: AGENT_DASHBOARD.attributionRevenueLeadMagnet,
  },
];

/* Match Dashboard.jsx font-injection convention. */
function FontInjector() {
  useEffect(() => {
    const id = "agent-cc-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Bungee+Shade&family=Major+Mono+Display&family=Special+Elite&family=VT323&display=swap";
    document.head.appendChild(link);
  }, []);
  return null;
}

async function fetchAgentDashboard() {
  const res = await axios.get(`${API}/agent-dashboard`, { timeout: 15_000 });
  return res.data;
}

async function fetchAttributionSummaries() {
  const results = await Promise.all(
    AGENT_CARDS.map((cfg) =>
      axios
        .get(`${API}/attribution/summary/${cfg.key}`, { timeout: 15_000 })
        .then((r) => r.data)
        .catch((e) => ({ __error: String(e?.message || e) }))
    )
  );
  return Object.fromEntries(AGENT_CARDS.map((cfg, i) => [cfg.key, results[i]]));
}

function formatCurrency(n) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatRate(r) {
  if (r === null || r === undefined || Number.isNaN(Number(r))) return "—";
  return `${(Number(r) * 100).toFixed(2)}%`;
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

/* Derive a compact status label from a single agent doc. */
function deriveStatus(doc) {
  if (!doc) return "empty";
  if (doc.status === "fetch_error") return "fetch_error";
  if (doc.status) return doc.status; // "ok" | "error" | "no_input"
  // market_signals doesn't set `status`; infer from source_status.
  const sources = doc.source_status;
  if (sources && typeof sources === "object") {
    const values = Object.values(sources);
    if (values.some((v) => v === "error")) return "error";
    return "ok";
  }
  return "ok";
}

function AgentCard({ config, doc }) {
  const status = deriveStatus(doc);
  const generatedAt = doc?.generated_at;
  const headline = doc?.analysis?.headline;

  return (
    <Card
      className={`agent-cc-card tone-${config.tone}`}
      data-testid={config.testCard}
    >
      <CardHeader className="p-0 pb-0">
        <CardTitle className="agent-cc-card__title">{config.label}</CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        <span
          className="agent-cc-status"
          data-status={status}
          data-testid={config.testStatus}
        >
          {status.replace(/_/g, " ")}
        </span>

        <div
          className="agent-cc-timestamp"
          data-testid={config.testGeneratedAt}
        >
          <span className="label">generated_at</span>
          {generatedAt ? formatTimestamp(generatedAt) : "— no runs yet —"}
        </div>

        <div
          className={`agent-cc-headline${headline ? "" : " empty"}`}
          data-testid={config.testHeadline}
        >
          <span className="label">headline</span>
          {headline || "Awaiting first run — no headline yet."}
        </div>
      </CardContent>
    </Card>
  );
}

function AttributionCard({ config, summary }) {
  const errored = summary && summary.__error;
  const clicks = errored ? "—" : Number(summary?.total_clicks ?? 0).toLocaleString();
  const conversions = errored ? "—" : Number(summary?.total_conversions ?? 0).toLocaleString();
  const rate = errored ? "—" : formatRate(summary?.conversion_rate);
  const revenue = errored ? "—" : formatCurrency(summary?.total_sale_amount);

  return (
    <Card
      className={`agent-cc-card agent-cc-attr-card tone-${config.tone}`}
      data-testid={config.testAttrCard}
    >
      <CardHeader className="p-0 pb-0">
        <CardTitle className="agent-cc-card__title">{config.label}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="agent-cc-stat-grid">
          <div className="agent-cc-stat">
            <div className="agent-cc-stat__label">Clicks</div>
            <div
              className="agent-cc-stat__value"
              data-testid={config.testAttrClicks}
            >
              {clicks}
            </div>
          </div>
          <div className="agent-cc-stat">
            <div className="agent-cc-stat__label">Conversions</div>
            <div
              className="agent-cc-stat__value"
              data-testid={config.testAttrConversions}
            >
              {conversions}
            </div>
          </div>
          <div className="agent-cc-stat">
            <div className="agent-cc-stat__label">Conv. Rate</div>
            <div
              className="agent-cc-stat__value"
              data-testid={config.testAttrRate}
            >
              {rate}
            </div>
          </div>
          <div className="agent-cc-stat">
            <div className="agent-cc-stat__label">Revenue</div>
            <div
              className="agent-cc-stat__value revenue"
              data-testid={config.testAttrRevenue}
            >
              {revenue}
            </div>
          </div>
        </div>
        {errored && (
          <div className="agent-cc-attr-error">{summary.__error}</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AgentDashboard() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ["agent-dashboard"],
    queryFn: fetchAgentDashboard,
    refetchInterval: 30_000,
  });

  const {
    data: attrData,
    isLoading: attrLoading,
    isError: attrIsError,
    error: attrError,
  } = useQuery({
    queryKey: ["attribution-summaries"],
    queryFn: fetchAttributionSummaries,
    refetchInterval: 30_000,
  });

  return (
    <div className="agent-cc-root" data-testid={AGENT_DASHBOARD.root}>
      <FontInjector />

      <header className="agent-cc-banner" data-testid={AGENT_DASHBOARD.banner}>
        <div>
          <h1>Revenue Agent Command Center</h1>
          <div className="subtitle">Live · Market · Offer · YouTube · Lead</div>
        </div>
        <div className="banner-actions">
          <Button
            type="button"
            className="agent-cc-refresh"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["agent-dashboard"] });
              queryClient.invalidateQueries({ queryKey: ["attribution-summaries"] });
              refetch();
            }}
            disabled={isFetching}
            data-testid={AGENT_DASHBOARD.refreshButton}
          >
            {isFetching ? "SYNCING…" : "↻ REFRESH"}
          </Button>
        </div>
      </header>

      {isLoading && (
        <div
          className="agent-cc-loading"
          data-testid={AGENT_DASHBOARD.loading}
        >
          Fetching latest agent reports
        </div>
      )}

      {isError && !isLoading && (
        <div className="agent-cc-error" data-testid={AGENT_DASHBOARD.error}>
          Failed to load agent dashboard: {String(error?.message || error)}
        </div>
      )}

      {!isLoading && !isError && (
        <div className="agent-cc-grid">
          {AGENT_CARDS.map((cfg) => (
            <AgentCard key={cfg.key} config={cfg} doc={data?.[cfg.key]} />
          ))}
        </div>
      )}

      <section
        className="agent-cc-attr-section"
        data-testid={AGENT_DASHBOARD.attributionSection}
      >
        <h2 className="agent-cc-section-title">Attribution Summary</h2>

        {attrLoading && (
          <div
            className="agent-cc-loading"
            data-testid={AGENT_DASHBOARD.attributionLoading}
          >
            Fetching attribution summaries
          </div>
        )}

        {attrIsError && !attrLoading && (
          <div
            className="agent-cc-error"
            data-testid={AGENT_DASHBOARD.attributionError}
          >
            Failed to load attribution summaries:{" "}
            {String(attrError?.message || attrError)}
          </div>
        )}

        {!attrLoading && !attrIsError && (
          <div className="agent-cc-grid">
            {AGENT_CARDS.map((cfg) => (
              <AttributionCard
                key={`attr-${cfg.key}`}
                config={cfg}
                summary={attrData?.[cfg.key]}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
